import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from "react";
import { useChainStore, useAccountStore, } from "../shared/store";
import { WalletType } from "../shared/wallet";
import { useConnect } from "./account";
import { getWallet } from "../wallet";
export const ChainProvider = (props) => {
    useEffect(() => {
        useChainStore.setState((prev) => ({
            chains: props.options?.cosmos?.chains ?? prev.chains,
            chainsConfig: props.options?.cosmos?.chainsConfig ?? prev.chainsConfig,
            options: props.options ?? prev.options,
        }));
    }, [props.options]);
    return (_jsxs(_Fragment, { children: [(props.onConnected || props.onDisconnected || props.onConnectError) && (_jsx(ConnectCallback, { ...props })), _jsx(ChainDependantComponents, {}), props.children] }));
};
export const setChainInfo = async (args) => {
    const chainId = args.chainId ?? useChainStore.getState().chainId;
    if (args.rpc) {
        const chains = [...useChainStore.getState().chains];
        const index = chains.findIndex((chain) => chain.chainId === chainId);
        if (index > -1 && chains[index].rpc !== args.rpc) {
            chains[index] = { ...chains[index], rpc: args.rpc };
            useChainStore.setState({ chains });
        }
    }
    if (args.chainId) {
        if (process.env.BRAND_ID === "levana") {
            const { setInjectiveWalletStrategy } = await import("../injective/wallet");
            await setInjectiveWalletStrategy(args.chainId);
        }
        // Must be set last
        useChainStore.setState({ chainId: args.chainId });
    }
};
const ConnectCallback = (props) => {
    const propsRef = useRef(props);
    useEffect(() => {
        propsRef.current = props;
    }, [props]);
    useEffect(() => {
        return useAccountStore.subscribe((state, previousState) => {
            if (propsRef.current.onConnected &&
                state.status === "connected" &&
                previousState.status !== "connected") {
                const { chainId } = useChainStore.getState();
                const account = state.activeAccount(chainId);
                if (account) {
                    propsRef.current.onConnected(account);
                }
            }
            if (propsRef.current.onDisconnected &&
                state.status === "disconnected" &&
                previousState.status === "connected") {
                propsRef.current.onDisconnected();
            }
            if (propsRef.current.onConnectError &&
                state.connectIssue &&
                state.connectIssue.error !== previousState.connectIssue?.error) {
                propsRef.current.onConnectError(state.connectIssue.error);
            }
        });
    }, []);
    return null;
};
/**
 * The `chainId` might be an empty string. In this case, related components
 * should wait to load until there's a valid value.
 */
const ChainDependantComponents = () => {
    const chainId = useChainStore((state) => state.chainId);
    if (chainId.length === 0) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsx(InitialViewingConnectStatus, {}), _jsx(ChangeAccountAutoConnect, {}), _jsx(CrossChainAutoConnect, {})] }));
};
const InitialViewingConnectStatus = () => {
    useEffect(() => {
        const { chainId } = useChainStore.getState();
        const account = useAccountStore.getState().activeAccount(chainId);
        if (account && account.walletType === WalletType.viewing) {
            useAccountStore.setState({ status: "connected" });
        }
    }, []);
    return null;
};
const ChangeAccountAutoConnect = () => {
    const walletType = useAccountStore((state) => state.walletType());
    const { connect } = useConnect();
    const connectRef = useRef(connect);
    useEffect(() => {
        connectRef.current = connect;
    }, [connect]);
    useEffect(() => {
        if (!walletType || walletType === WalletType.viewing) {
            return;
        }
        const provider = getWallet(walletType).provider();
        return provider.subscription?.(() => connectRef.current({ walletType }));
    }, [walletType]);
    return null;
};
const CrossChainAutoConnect = () => {
    const { connect } = useConnect();
    const connectRef = useRef(connect);
    useEffect(() => {
        connectRef.current = connect;
    }, [connect]);
    const chainId = useChainStore((state) => state.chainId);
    useEffect(() => {
        if (chainId.length === 0) {
            return;
        }
        const walletType = useAccountStore.getState().walletType();
        if (walletType && walletType !== WalletType.viewing) {
            connectRef.current({ walletType });
        }
    }, [chainId]);
    return null;
};
//# sourceMappingURL=provider.js.map